import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  ButtonGroup,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SchemaIcon from '@mui/icons-material/Schema';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

/**
 * onChange(patch) donde patch puede ser string (ruta escalar) o
 * { jsonPath, jsonMode, arrayFilters?, unnestFields?, objectValueAgg? }
 */
export default function JsonPathAssistant({
  connectionId,
  database,
  tableId,
  column,
  value = '',
  jsonMode = 'scalar',
  arrayFilters = [],
  unnestFields = [],
  arrayValueField = '',
  objectValueAgg = 'sum',
  arrayElemField = '',
  lookup = null,
  onChange,
  getAuthHeaders,
  catalogPaths = null,
  size = 'small',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [livePaths, setLivePaths] = useState([]);
  const [samplesChecked, setSamplesChecked] = useState(0);
  const [source, setSource] = useState('model'); // 'model' | 'merged'
  const [pendingArray, setPendingArray] = useState(null);

  const canExplore = Boolean(connectionId && database && tableId && column && getAuthHeaders);
  const modelPaths = useMemo(
    () => (Array.isArray(catalogPaths) ? catalogPaths : []),
    [catalogPaths]
  );

  const paths = useMemo(() => {
    if (source === 'merged' && livePaths.length) {
      return mergePathLists(modelPaths, livePaths);
    }
    if (modelPaths.length) return modelPaths;
    return livePaths;
  }, [source, modelPaths, livePaths]);

  useEffect(() => {
    setOpen(false);
    setLivePaths([]);
    setSamplesChecked(0);
    setError(null);
    setPendingArray(null);
    setSource(modelPaths.length ? 'model' : 'merged');
  }, [tableId, column, modelPaths]);

  const emit = (patch) => {
    if (typeof patch === 'string') {
      onChange?.({ jsonPath: patch, jsonMode: 'scalar', arrayFilters: [], unnestFields: [] });
    } else {
      onChange?.(patch);
    }
  };

  const showModelPaths = () => {
    setOpen(true);
    setSource('model');
    setError(null);
    setPendingArray(null);
  };

  const jsonPathsMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/json-paths', body, { headers }),
  });

  const explore = useCallback(async () => {
    if (!canExplore) {
      showModelPaths();
      return;
    }
    setOpen(true);
    setLoading(true);
    setError(null);
    setPendingArray(null);
    setSource(modelPaths.length ? 'merged' : 'merged');
    try {
      const headers = getAuthHeaders();
      const res = await jsonPathsMutation.mutateAsync({
        body: { connectionId, database, tableId, column, limit: 20 },
        headers,
      });
      if (res.data.success) {
        setLivePaths(res.data.paths || []);
        setSamplesChecked(res.data.samplesChecked || 0);
      } else {
        setError(res.data.error || 'No se pudieron inferir rutas');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al explorar JSON');
    } finally {
      setLoading(false);
    }
  }, [canExplore, connectionId, database, tableId, column, getAuthHeaders, modelPaths.length]);

  const pickScalar = (path) => {
    emit({ jsonPath: path, jsonMode: 'scalar', arrayFilters: [], unnestFields: [] });
    setPendingArray(null);
  };

  const pickArrayCount = (entry) => {
    emit({
      jsonPath: entry.path,
      jsonMode: 'arrayCount',
      arrayFilters: arrayFilters?.length ? arrayFilters : [],
      unnestFields: [],
    });
    setPendingArray(entry);
  };

  const pickArraySum = (entry, fieldPath = 'probability') => {
    emit({
      jsonPath: entry.path,
      jsonMode: 'arraySum',
      arrayValueField: fieldPath,
      arrayFilters: arrayFilters?.length ? arrayFilters : [],
      unnestFields: [],
    });
    setPendingArray(entry);
  };

  const pickArrayUnnest = (entry) => {
    const itemPaths = entry.itemPaths || [];
    const byPath = (suffix) =>
      itemPaths.find(
        (p) =>
          p.selectable &&
          (p.path === suffix || p.path.endsWith(`.${suffix}`) || p.path.endsWith(suffix))
      );
    const fx = byPath('frame.x') || byPath('x');
    const fy = byPath('frame.y') || byPath('y');
    let fields = [];
    if (fx) fields.push({ path: fx.path, alias: 'x' });
    if (fy) fields.push({ path: fy.path, alias: 'y' });
    if (!fields.length) {
      fields = [
        { path: 'frame.x', alias: 'x' },
        { path: 'frame.y', alias: 'y' },
      ];
    }
    emit({
      jsonPath: entry.path,
      jsonMode: 'arrayUnnest',
      arrayFilters: arrayFilters?.length ? arrayFilters : [],
      unnestFields: fields,
    });
    setPendingArray(entry);
  };

  const pickArrayGroup = (entry) => {
    emit({
      jsonPath: entry.path,
      jsonMode: 'arrayUnnestGroup',
      arrayFilters: [],
      unnestFields: [],
      arrayElemField: arrayElemField || '',
      lookup: lookup || null,
    });
    setPendingArray(entry);
  };

  const pickObjectKv = (entry) => {
    emit({
      jsonPath: entry.path,
      jsonMode: 'objectKv',
      arrayFilters: [],
      unnestFields: [],
      objectValueAgg: objectValueAgg || 'sum',
    });
    setPendingArray(entry);
  };

  const modeLabel =
    jsonMode === 'arrayCount'
      ? 'Contar elementos del array'
      : jsonMode === 'arraySum'
        ? `Sumar campo «${arrayValueField || '?'}» en cada fila`
        : jsonMode === 'arrayUnnest'
          ? 'Expandir → una fila por elemento'
          : jsonMode === 'arrayUnnestGroup'
            ? `Agrupar por «${arrayElemField || '?'}»${lookup?.targetTableId ? ' → buscar en ' + lookup.targetTableId.split('.').pop() : ''}`
            : jsonMode === 'objectKv'
              ? isWildcardPropertyPath(value)
                ? `Extrae «${String(value).split('.').pop()}» de cada clave (person, car…)`
                : 'Objeto → clave/valor (incoming, outgoing…)'
              : 'Escalar';

  const quickOptions = paths.filter(
    (p) => p.selectable || (p.types || []).includes('array') || (p.modes || []).includes('objectKv')
  );

  const arrayAtPath =
    pendingArray?.path === value
      ? pendingArray
      : paths.find((p) => p.path === value && (p.types || []).includes('array')) || null;
  const objectAtPath =
    pendingArray?.path === value
      ? pendingArray
      : paths.find(
          (p) =>
            p.path === value &&
            (p.types || []).includes('object') &&
            !(p.types || []).includes('array')
        ) || null;
  const showArrayModePicker = jsonMode === 'scalar' && Boolean(value) && Boolean(arrayAtPath);
  const showObjectModePicker = jsonMode === 'scalar' && Boolean(value) && Boolean(objectAtPath);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <TextField
          size={size}
          fullWidth
          label="Ruta JSON"
          placeholder="ej. detections o objects"
          value={value || ''}
          disabled={disabled}
          onChange={(e) => {
            const jsonPath = e.target.value;
            const nextWild = isWildcardPropertyPath(jsonPath);
            const wasWild = isWildcardPropertyPath(value);
            emit({
              jsonPath,
              jsonMode: nextWild ? 'objectKv' : wasWild ? 'scalar' : jsonMode || 'scalar',
              arrayFilters,
              unnestFields,
            });
          }}
          helperText={
            !value && jsonMode === 'scalar'
              ? modelPaths.length
                ? 'Elige una ruta del modelo o escribe a mano.'
                : 'Vacío = JSON crudo. Usa Explorar o carga ejemplos en el modelo.'
              : `${modeLabel}${jsonMode !== 'scalar' ? ' · Postgres/Timescale' : ''}`
          }
          sx={{ flex: '1 1 160px', minWidth: 140 }}
        />

        {quickOptions.length > 0 && (
          <FormControl size={size} sx={{ minWidth: 160, mt: 0.5 }}>
            <InputLabel>Probar ruta</InputLabel>
            <Select
              label="Probar ruta"
              value={quickOptions.some((p) => p.path === value) ? value : ''}
              disabled={disabled}
              onChange={(e) => {
                const entry = paths.find((p) => p.path === e.target.value);
                if (!entry) return;
                setOpen(true);
                setPendingArray((entry.types || []).includes('array') ? entry : null);
                if ((entry.types || []).includes('array')) {
                  // Solo fija la ruta; el usuario elige Contar/Expandir abajo o en Explorar
                  emit({
                    jsonPath: entry.path,
                    jsonMode: 'scalar',
                    arrayFilters: [],
                    unnestFields: [],
                  });
                } else if ((entry.modes || []).includes('objectKv') && isWildcardPropertyPath(entry.path)) {
                  pickObjectKv(entry);
                } else if (entry.selectable) {
                  pickScalar(entry.path);
                } else {
                  emit({
                    jsonPath: entry.path,
                    jsonMode: 'scalar',
                    arrayFilters: [],
                    unnestFields: [],
                  });
                }
              }}
            >
              <MenuItem value="">
                <em>Elegir…</em>
              </MenuItem>
              {quickOptions.map((p) => (
                <MenuItem key={p.path} value={p.path}>
                  {p.path}
                  {(p.types || []).includes('array') ? ' []' : ''}
                  {p.sources?.includes('model') || (!livePaths.length && modelPaths.length)
                    ? ' · modelo'
                    : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <ButtonGroup size="small" sx={{ mt: 0.5, flexShrink: 0 }}>
          {modelPaths.length > 0 && (
            <Button
              variant={open && source === 'model' ? 'contained' : 'outlined'}
              startIcon={<SchemaIcon />}
              disabled={disabled}
              onClick={() => {
                if (open && source === 'model') setOpen(false);
                else showModelPaths();
              }}
            >
              Modelo
            </Button>
          )}
          <Button
            variant={open && source === 'merged' ? 'contained' : 'outlined'}
            startIcon={open && source === 'merged' ? <ExpandLessIcon /> : <TravelExploreIcon />}
            disabled={disabled || !canExplore}
            onClick={() => {
              if (open && source === 'merged' && !loading) {
                setOpen(false);
                return;
              }
              explore();
            }}
          >
            {loading ? '…' : 'Explorar'}
          </Button>
        </ButtonGroup>
      </Box>

      {open && (
        <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
          <Box sx={{ px: 1.25, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {loading
                ? 'Analizando muestra de la BD…'
                : source === 'model'
                  ? `${modelPaths.length} rutas del modelo — pulsa Contar / Expandir / Usar`
                  : `${samplesChecked || 0} JSON de muestra${
                      modelPaths.length ? ` · +${modelPaths.length} del modelo` : ''
                    }`}
            </Typography>
          </Box>
          {loading && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {!loading && error && (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
            </Alert>
          )}
          {!loading && !error && paths.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
              No hay rutas. Carga ejemplos en el modelo o Explorar la BD.
            </Typography>
          )}
          {!loading &&
            paths.map((p) => {
              const selected = value === p.path;
              const isArray = (p.types || []).includes('array');
              const typeLabel = (p.types || []).filter((t) => t !== 'null').join('|') || '—';
              const fromModel =
                p.sources?.includes('model') ||
                (source === 'model' && modelPaths.some((m) => m.path === p.path));
              return (
                <Box
                  key={p.path}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontFamily: 'monospace', fontSize: 12 }}
                    >
                      {p.path}
                    </Typography>
                    <Chip size="small" label={typeLabel} sx={{ height: 18, fontSize: 10 }} />
                    {fromModel && (
                      <Chip
                        size="small"
                        color="secondary"
                        variant="outlined"
                        label="modelo"
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    )}
                  </Box>
                  {p.hint && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {p.hint}
                    </Typography>
                  )}
                  {isArray ? (
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      <Button size="small" variant="contained" onClick={() => pickArrayCount(p)}>
                        Contar elementos
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          pickArraySum(
                            p,
                            (p.itemPaths || []).find((ip) => ip.path === 'probability')?.path ||
                              (p.itemPaths || []).find((ip) => ip.path === 'frame.x')?.path ||
                              'probability'
                          )
                        }
                      >
                        Sumar campo
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => pickArrayUnnest(p)}>
                        Expandir a filas
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => pickArrayGroup(p)}>
                        Agrupar y buscar
                      </Button>
                    </Box>
                  ) : (p.modes || []).includes('objectKv') && isWildcardPropertyPath(p.path) ? (
                    <Button size="small" variant="contained" onClick={() => pickObjectKv(p)}>
                      Extraer {String(p.path).split('.').pop()}
                    </Button>
                  ) : (p.modes || []).includes('objectKv') &&
                    (p.types || []).includes('object') &&
                    !(p.types || []).includes('array') ? (
                    <Button size="small" variant="contained" onClick={() => pickObjectKv(p)}>
                      Clave / valor
                    </Button>
                  ) : p.selectable ? (
                    <Button size="small" variant="contained" onClick={() => pickScalar(p.path)}>
                      Usar esta ruta
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      onClick={() =>
                        emit({
                          jsonPath: p.path,
                          jsonMode: 'scalar',
                          arrayFilters: [],
                          unnestFields: [],
                        })
                      }
                    >
                      Fijar ruta
                    </Button>
                  )}
                </Box>
              );
            })}
        </Paper>
      )}

      {showObjectModePicker && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="caption" display="block" sx={{ mb: 0.75 }}>
            «{value}» es un objeto (p. ej. incoming/outgoing). Convierte a filas clave/valor:
          </Typography>
          <Button size="small" variant="contained" onClick={() => pickObjectKv(objectAtPath)}>
            Clave / valor
          </Button>
        </Alert>
      )}

      {showArrayModePicker && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="caption" display="block" sx={{ mb: 0.75 }}>
            «{value}» es un array. Elige cómo usarlo:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Button size="small" variant="contained" onClick={() => pickArrayCount(arrayAtPath)}>
              Contar elementos
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                pickArraySum(
                  arrayAtPath,
                  (arrayAtPath.itemPaths || []).find((ip) => ip.path === 'probability')?.path ||
                    'probability'
                )
              }
            >
              Sumar campo
            </Button>
            <Button size="small" variant="outlined" onClick={() => pickArrayUnnest(arrayAtPath)}>
              Expandir a filas
            </Button>
            <Button size="small" variant="outlined" onClick={() => pickArrayGroup(arrayAtPath)}>
              Agrupar y buscar
            </Button>
          </Box>
        </Alert>
      )}

      {(jsonMode === 'arrayCount' || jsonMode === 'arraySum' || jsonMode === 'arrayUnnest') && (
        <ArrayElementConfig
          jsonMode={jsonMode}
          arrayFilters={arrayFilters}
          unnestFields={unnestFields}
          arrayValueField={arrayValueField}
          itemPaths={
            pendingArray?.itemPaths ||
            paths.find((p) => p.path === value)?.itemPaths ||
            []
          }
          onChangeFilters={(nextFilters) =>
            emit({ jsonPath: value, jsonMode, arrayFilters: nextFilters, unnestFields, arrayValueField })
          }
          onChangeUnnest={(nextUnnest) =>
            emit({ jsonPath: value, jsonMode, arrayFilters, unnestFields: nextUnnest, arrayValueField })
          }
          onChangeValueField={(nextField) =>
            emit({ jsonPath: value, jsonMode, arrayFilters, unnestFields, arrayValueField: nextField })
          }
        />
      )}

      {jsonMode === 'objectKv' && (
        <ObjectKvConfig
          jsonPath={value}
          objectValueAgg={objectValueAgg || 'sum'}
          onChangeAgg={(nextAgg) =>
            emit({
              jsonPath: value,
              jsonMode: 'objectKv',
              arrayFilters: [],
              unnestFields: [],
              objectValueAgg: nextAgg,
            })
          }
        />
      )}

      {jsonMode === 'arrayUnnestGroup' && (
        <ArrayGroupConfig
          arrayElemField={arrayElemField}
          lookup={lookup}
          itemPaths={
            pendingArray?.itemPaths ||
            paths.find((p) => p.path === value)?.itemPaths ||
            []
          }
          onChangeElemField={(nextField) =>
            emit({ jsonPath: value, jsonMode: 'arrayUnnestGroup', arrayElemField: nextField, lookup })
          }
          onChangeLookup={(nextLookup) =>
            emit({ jsonPath: value, jsonMode: 'arrayUnnestGroup', arrayElemField, lookup: nextLookup })
          }
        />
      )}
    </Box>
  );
}

const OBJECT_VALUE_AGG_OPTIONS = [
  { value: 'sum', label: 'Suma — total si el valor es array' },
  { value: 'count', label: 'Conteo — cantidad de elementos' },
  { value: 'avg', label: 'Promedio' },
  { value: 'min', label: 'Mínimo' },
  { value: 'max', label: 'Máximo' },
  { value: 'median', label: 'Mediana' },
];

function isWildcardPropertyPath(jsonPath) {
  const raw = String(jsonPath || '').trim().replace(/^\$\.?/, '');
  const parts = raw.split('.').filter(Boolean);
  if (parts.filter((p) => p === '*').length !== 1) return false;
  const star = parts.indexOf('*');
  const property = parts.slice(star + 1);
  const prefix = parts.slice(0, star);
  const ident = (s) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(s);
  return property.length > 0 && property.every(ident) && prefix.every(ident);
}

function ObjectKvConfig({ jsonPath = '', objectValueAgg = 'sum', onChangeAgg }) {
  const wildcard = isWildcardPropertyPath(jsonPath);
  const property = String(jsonPath || '').split('.').pop();
  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
        {wildcard ? (
          <>
            <strong>*.{property}:</strong> cada objeto que tenga «{property}» genera una fila.
            <strong> kv_key</strong> es la clave (person, car…) y <strong>kv_value</strong> el número.
          </>
        ) : (
          <>
            <strong>Clave/valor:</strong> cada clave del objeto (incoming, outgoing…) genera una fila. En
            gráficos de líneas usa <strong>kv_value</strong> como medida y <strong>kv_key</strong> como
            series.
          </>
        )}
      </Alert>
      <FormControl size="small" fullWidth>
        <InputLabel>Si el valor es array</InputLabel>
        <Select
          label="Si el valor es array"
          value={objectValueAgg || 'sum'}
          onChange={(e) => onChangeAgg?.(e.target.value)}
        >
          {OBJECT_VALUE_AGG_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Paper>
  );
}

function ArrayGroupConfig({
  arrayElemField = '',
  lookup = null,
  itemPaths = [],
  onChangeElemField,
  onChangeLookup,
}) {
  const fieldChoices = itemPaths.length
    ? itemPaths.map((p) => p.path)
    : ['userId', 'name', 'id'];
  const lookupEnabled = Boolean(lookup);

  const patchLookup = (patch) => {
    onChangeLookup?.({
      targetTableId: '',
      targetKeyColumn: '',
      displayColumn: '',
      ...(lookup || {}),
      ...patch,
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
        <strong>Agrupar por campo de array:</strong> saca un campo de cada elemento (ej. userId de
        cada comentario) y lo usa como Fila para agrupar/contar. Opcional: busca su nombre en otra
        tabla (como un Merge/Join de Power Query).
      </Alert>
      <FormControl size="small" fullWidth sx={{ mb: 1 }}>
        <InputLabel>Campo del elemento a agrupar</InputLabel>
        <Select
          label="Campo del elemento a agrupar"
          value={arrayElemField || ''}
          onChange={(e) => onChangeElemField?.(e.target.value)}
        >
          {fieldChoices.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
          {arrayElemField && !fieldChoices.includes(arrayElemField) && (
            <MenuItem value={arrayElemField}>{arrayElemField}</MenuItem>
          )}
        </Select>
      </FormControl>
      <TextField
        size="small"
        fullWidth
        label="O escribe el campo manualmente"
        value={arrayElemField || ''}
        onChange={(e) => onChangeElemField?.(e.target.value)}
        placeholder="ej. userId"
        sx={{ mb: 1 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={lookupEnabled}
            onChange={(e) =>
              onChangeLookup?.(
                e.target.checked
                  ? { targetTableId: '', targetKeyColumn: '', displayColumn: '' }
                  : null
              )
            }
          />
        }
        label={<Typography variant="body2">Buscar el valor en otra tabla (ej. resolver el nombre)</Typography>}
      />
      {lookupEnabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5, pl: 1 }}>
          <TextField
            size="small"
            fullWidth
            label="Tabla donde buscar (ej. public.vsaas_users)"
            value={lookup?.targetTableId || ''}
            onChange={(e) => patchLookup({ targetTableId: e.target.value })}
          />
          <TextField
            size="small"
            fullWidth
            label="Columna de coincidencia (ej. user_id)"
            value={lookup?.targetKeyColumn || ''}
            onChange={(e) => patchLookup({ targetKeyColumn: e.target.value })}
          />
          <TextField
            size="small"
            fullWidth
            label="Columna a mostrar (ej. name)"
            value={lookup?.displayColumn || ''}
            onChange={(e) => patchLookup({ displayColumn: e.target.value })}
          />
          <Typography variant="caption" color="text.secondary">
            Si ambas tablas tienen customer_id, se agrega esa condición automáticamente.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function mergePathLists(modelPaths, livePaths) {
  const byPath = new Map();
  const ingest = (list, src) => {
    for (const p of list || []) {
      if (!p?.path) continue;
      const prev = byPath.get(p.path);
      if (!prev) {
        byPath.set(p.path, { ...p, sources: [src] });
        continue;
      }
      const types = [...new Set([...(prev.types || []), ...(p.types || [])])];
      const itemPaths = [...(prev.itemPaths || [])];
      for (const ip of p.itemPaths || []) {
        if (!itemPaths.some((x) => x.path === ip.path)) itemPaths.push(ip);
      }
      byPath.set(p.path, {
        ...prev,
        ...p,
        types,
        count: Math.max(prev.count || 0, p.count || 0),
        itemPaths: itemPaths.length ? itemPaths : prev.itemPaths,
        selectable: Boolean(prev.selectable || p.selectable),
        modes: [...new Set([...(prev.modes || []), ...(p.modes || [])])],
        sources: [...new Set([...(prev.sources || []), src])],
        hint: prev.hint || p.hint,
      });
    }
  };
  ingest(modelPaths, 'model');
  ingest(livePaths, 'sample');
  return [...byPath.values()].sort((a, b) => {
    if (a.selectable !== b.selectable) return a.selectable ? -1 : 1;
    const aArr = (a.types || []).includes('array') ? 1 : 0;
    const bArr = (b.types || []).includes('array') ? 1 : 0;
    if (aArr !== bArr) return bArr - aArr;
    return a.path.localeCompare(b.path);
  });
}

function ArrayElementConfig({
  jsonMode,
  arrayFilters = [],
  unnestFields = [],
  arrayValueField = '',
  itemPaths = [],
  onChangeFilters,
  onChangeUnnest,
  onChangeValueField,
}) {
  const [path, setPath] = useState('class');
  const [operator, setOperator] = useState('eq');
  const [filterValue, setFilterValue] = useState('');

  const addFilter = () => {
    if (!path.trim()) return;
    onChangeFilters([
      ...arrayFilters,
      { path: path.trim(), operator, value: filterValue },
    ]);
    setFilterValue('');
  };

  const selectedUnnest = new Set((unnestFields || []).map((f) => f.path));

  const toggleUnnest = (ip) => {
    if (selectedUnnest.has(ip.path)) {
      onChangeUnnest(unnestFields.filter((f) => f.path !== ip.path));
    } else {
      let path = ip.path;
      if (path === 'x') path = 'frame.x';
      else if (path === 'y') path = 'frame.y';
      const alias =
        path.endsWith('.x') || path === 'x'
          ? 'x'
          : path.endsWith('.y') || path === 'y'
            ? 'y'
            : undefined;
      onChangeUnnest([...unnestFields, { path, alias }]);
    }
  };

  const fieldChoices = itemPaths.length
    ? itemPaths.map((p) => p.path)
    : ['class', 'direction', 'probability', 'frame.x', 'frame.y'];

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
        {jsonMode === 'arrayCount' ? (
          <>
            <strong>Contar:</strong> número de elementos que cumplen los filtros (ej. class=person).
          </>
        ) : jsonMode === 'arraySum' ? (
          <>
            <strong>Sumar campo:</strong> agrega probability, frame.x, etc. de cada elemento del array
            en cada fila (luego SUM/AVG al agrupar).
          </>
        ) : (
          <>
            <strong>Expandir:</strong> marca campos a sacar (frame.x, frame.y). Una fila por
            elemento.
          </>
        )}
      </Alert>
      {jsonMode === 'arraySum' && (
        <FormControl size="small" fullWidth sx={{ mb: 1 }}>
          <InputLabel>Campo numérico a sumar</InputLabel>
          <Select
            label="Campo numérico a sumar"
            value={arrayValueField || ''}
            onChange={(e) => onChangeValueField?.(e.target.value)}
          >
            {fieldChoices.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
            {arrayValueField && !fieldChoices.includes(arrayValueField) && (
              <MenuItem value={arrayValueField}>{arrayValueField}</MenuItem>
            )}
          </Select>
        </FormControl>
      )}
      <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
        {jsonMode === 'arrayCount' ? 'Filtros sobre cada elemento' : 'Campos a sacar + filtros'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {(arrayFilters || []).map((f, i) => (
          <Chip
            key={`${f.path}-${i}`}
            size="small"
            label={`${f.path} ${f.operator} ${f.value ?? ''}`}
            onDelete={() => onChangeFilters(arrayFilters.filter((_, j) => j !== i))}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1, alignItems: 'center' }}>
        <FormControl size="small" sx={{ flex: '1 1 120px', minWidth: 120 }}>
          <InputLabel>Campo</InputLabel>
          <Select label="Campo" value={path} onChange={(e) => setPath(e.target.value)}>
            {fieldChoices.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
            {!fieldChoices.includes(path) && path && <MenuItem value={path}>{path}</MenuItem>}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: '0 1 110px', minWidth: 100 }}>
          <InputLabel>Op</InputLabel>
          <Select label="Op" value={operator} onChange={(e) => setOperator(e.target.value)}>
            <MenuItem value="eq">=</MenuItem>
            <MenuItem value="ne">≠</MenuItem>
            <MenuItem value="contains">contiene</MenuItem>
            <MenuItem value="isNull">nulo</MenuItem>
            <MenuItem value="isNotNull">no nulo</MenuItem>
          </Select>
        </FormControl>
        {operator !== 'isNull' && operator !== 'isNotNull' && (
          <TextField
            size="small"
            label="Valor"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            sx={{ flex: '2 1 160px', minWidth: 140 }}
            placeholder="person"
          />
        )}
        <Button size="small" onClick={addFilter} sx={{ flexShrink: 0 }}>
          + Filtro
        </Button>
      </Box>

      {jsonMode === 'arrayUnnest' && (
        <FormGroup>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            Proyectar campos del elemento:
          </Typography>
          {(itemPaths.filter((p) => p.selectable).length
            ? itemPaths.filter((p) => p.selectable)
            : [
                { path: 'frame.x', selectable: true },
                { path: 'frame.y', selectable: true },
                { path: 'class', selectable: true },
              ]
          ).map((ip) => (
            <FormControlLabel
              key={ip.path}
              control={
                <Checkbox
                  size="small"
                  checked={selectedUnnest.has(ip.path)}
                  onChange={() => toggleUnnest(ip)}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {ip.path}
                </Typography>
              }
            />
          ))}
        </FormGroup>
      )}
    </Paper>
  );
}
